import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  ShoppingCart, 
  Trash2, 
  Package, 
  Music, 
  Video,
  ArrowRight,
  ArrowLeft,
  LogIn
} from "lucide-react";
import { toast } from "sonner";

// Guest cart functions
const getGuestCart = () => {
  try {
    return JSON.parse(localStorage.getItem("guest_cart") || "[]");
  } catch {
    return [];
  }
};

const removeFromGuestCart = (itemType, itemId) => {
  const cart = getGuestCart();
  const newCart = cart.filter(item => !(item.item_type === itemType && item.item_id === itemId));
  localStorage.setItem("guest_cart", JSON.stringify(newCart));
  window.dispatchEvent(new Event("cart-updated"));
};

const clearGuestCart = () => {
  localStorage.removeItem("guest_cart");
  window.dispatchEvent(new Event("cart-updated"));
};

// Sync guest cart to server after login
export const syncGuestCartToServer = async (token) => {
  const guestCart = getGuestCart();
  if (guestCart.length === 0) return;

  for (const item of guestCart) {
    try {
      await axios.post(`${API}/cart/add`, item, {
        
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
    } catch (e) {
      // Item might already exist, ignore
    }
  }
  clearGuestCart();
};

export default function CartPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    fetchCart();
  }, [user]);

  const fetchCart = async () => {
    setLoading(true);
    
    if (user) {
      // Sync guest cart first if user just logged in
      await syncGuestCartToServer(token);
      
      try {
        const response = await axios.get(`${API}/cart`, {
          
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        setCart(response.data);
      } catch (error) {
        console.error("Failed to fetch cart", error);
        setCart({ items: [], total: 0 });
      }
    } else {
      // Build cart from localStorage for guests
      const guestCart = getGuestCart();
      const populatedItems = [];
      let total = 0;

      for (const item of guestCart) {
        try {
          if (item.item_type === "product") {
            const response = await axios.get(`${API}/products/${item.item_id}`);
            populatedItems.push({ item_type: "product", item_id: item.item_id, item: response.data });
            total += response.data.price;
          } else {
            const response = await axios.get(`${API}/bundles/${item.item_id}`);
            populatedItems.push({ item_type: "bundle", item_id: item.item_id, item: response.data });
            total += response.data.final_price;
          }
        } catch (e) {
          // Item no longer exists, remove from cart
          removeFromGuestCart(item.item_type, item.item_id);
        }
      }

      setCart({ items: populatedItems, total: Math.round(total * 100) / 100 });
    }
    
    setLoading(false);
  };

  const removeItem = async (itemType, itemId) => {
    if (user) {
      try {
        await axios.delete(`${API}/cart/remove/${itemType}/${itemId}`, {
          
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        toast.success("Item removed from cart");
        fetchCart();
      } catch (error) {
        toast.error("Failed to remove item");
      }
    } else {
      removeFromGuestCart(itemType, itemId);
      toast.success("Item removed from cart");
      fetchCart();
    }
  };

  const clearCart = async () => {
    if (user) {
      try {
        await axios.delete(`${API}/cart/clear`, {
          
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        toast.success("Cart cleared");
        fetchCart();
      } catch (error) {
        toast.error("Failed to clear cart");
      }
    } else {
      clearGuestCart();
      toast.success("Cart cleared");
      fetchCart();
    }
  };

  const handleCheckout = async () => {
    // If not logged in, redirect to login with return URL
    if (!user) {
      toast.info("Please login or create an account to complete your purchase");
      navigate("/login", { state: { from: { pathname: "/cart" } } });
      return;
    }

    setCheckingOut(true);
    try {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/checkout/session`, 
        { origin_url: originUrl },
        {  headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      
      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to start checkout");
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-48" />
            <div className="h-32 bg-muted rounded" />
            <div className="h-32 bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <ShoppingCart className="h-8 w-8 text-gold" />
              <h1 className="font-serif text-3xl font-bold">Your Cart</h1>
            </div>
            <p className="text-muted-foreground">
              {cart.items.length} item{cart.items.length !== 1 ? "s" : ""} in your cart
            </p>
          </div>
          
          {cart.items.length > 0 && (
            <Button variant="ghost" className="text-destructive" onClick={clearCart}>
              <Trash2 className="h-4 w-4 mr-2" />
              Clear Cart
            </Button>
          )}
        </div>

        {cart.items.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingCart className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-serif text-2xl font-semibold mb-2">Your cart is empty</h2>
            <p className="text-muted-foreground mb-8">
              Browse our courses and add some to your cart
            </p>
            <Button 
              className="bg-gold text-primary-foreground hover:bg-gold/90"
              onClick={() => navigate("/browse")}
              data-testid="browse-courses-btn"
            >
              Browse Courses
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map((item, index) => (
                <Card key={index} className="bg-card border-border" data-testid={`cart-item-${index}`}>
                  <CardContent className="p-4 flex gap-4">
                    {/* Thumbnail */}
                    <Link 
                      to={item.item_type === "product" ? `/product/${item.item_id}` : `/bundle/${item.item_id}`}
                      className="flex-shrink-0"
                    >
                      <div className="w-24 h-24 rounded overflow-hidden">
                        <img
                          src={item.item?.thumbnail_url || "https://images.unsplash.com/photo-1505664194779-8beaceb93744?w=200"}
                          alt={item.item?.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </Link>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            {item.item_type === "bundle" ? (
                              <Package className="h-4 w-4 text-gold" />
                            ) : item.item?.is_video ? (
                              <Video className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <Music className="h-4 w-4 text-muted-foreground" />
                            )}
                            <span className="text-xs text-muted-foreground uppercase">
                              {item.item_type}
                            </span>
                          </div>
                          <Link 
                            to={item.item_type === "product" ? `/product/${item.item_id}` : `/bundle/${item.item_id}`}
                          >
                            <h3 className="font-medium hover:text-gold transition-colors">
                              {item.item?.title}
                            </h3>
                          </Link>
                          {item.item_type === "bundle" && item.item?.products && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {item.item.products.length} topics included
                            </p>
                          )}
                          {item.item?.course_name && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {item.item.course_name}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-gold">
                            ${item.item_type === "bundle" 
                              ? item.item?.final_price?.toFixed(2) 
                              : item.item?.price?.toFixed(2)}
                          </p>
                          {item.item_type === "bundle" && item.item?.original_price > item.item?.final_price && (
                            <p className="text-sm text-muted-foreground line-through">
                              ${item.item?.original_price?.toFixed(2)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Remove */}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="flex-shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeItem(item.item_type, item.item_id)}
                      data-testid={`remove-item-${index}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Order Summary */}
            <div>
              <Card className="sticky top-24 bg-card border-2 border-gold">
                <CardContent className="p-6">
                  <h2 className="font-serif text-xl font-semibold mb-6">Order Summary</h2>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span>${cart.total.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Tax</span>
                      <span>$0.00</span>
                    </div>
                    <hr className="border-border" />
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total</span>
                      <span className="text-gold" data-testid="cart-total">${cart.total.toFixed(2)}</span>
                    </div>
                  </div>

                  {!user && (
                    <div className="mb-4 p-3 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground flex items-center gap-2">
                        <LogIn className="h-4 w-4" />
                        You'll need to login to checkout
                      </p>
                    </div>
                  )}

                  <Button
                    size="lg"
                    className="w-full bg-gold text-primary-foreground hover:bg-gold/90"
                    onClick={handleCheckout}
                    disabled={checkingOut}
                    data-testid="checkout-btn"
                  >
                    {checkingOut ? "Processing..." : user ? "Proceed to Checkout" : "Login to Checkout"}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>

                  <p className="text-xs text-muted-foreground text-center mt-4">
                    Secure checkout powered by Stripe
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Continue Shopping */}
        {cart.items.length > 0 && (
          <div className="mt-8">
            <Button variant="ghost" onClick={() => navigate("/browse")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Continue Shopping
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
